import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Sélecteur en cascade : Catégorie principale → Sous-catégorie.
 * Charge /categories (avec subcategories inclus) une seule fois.
 * Props :
 *   value         : { category_id, subcategory_id }
 *   onChange      : fn({ category_id, subcategory_id })
 *   required      : bool (ajoute l'astérisque, pas de validation HTML)
 *   testIdPrefix  : préfixe pour les data-testid (ex. "offer")
 *   layout        : "grid" (par défaut) ou "stack"
 */
export default function CategorySubcategoryPicker({
  value = {},
  onChange,
  required = false,
  testIdPrefix = "picker",
  layout = "grid",
}) {
  const [cats, setCats] = useState([]);
  const catId = value.category_id || "";
  const subId = value.subcategory_id || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data || []));
  }, []);

  const subs = useMemo(() => {
    const c = cats.find((x) => x.id === catId);
    return c?.subcategories || [];
  }, [cats, catId]);

  // Si la catégorie change, invalider la sous-catégorie si elle n'appartient pas à la nouvelle
  useEffect(() => {
    if (!catId || !subId) return;
    const stillValid = subs.some((s) => s.id === subId);
    if (!stillValid) onChange?.({ category_id: catId, subcategory_id: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catId, subs.length]);

  const wrapCls = layout === "stack" ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-3";
  const star = required ? <span className="text-rose-600 ml-0.5">*</span> : null;

  return (
    <div className={wrapCls} data-testid={`${testIdPrefix}-cat-sub-picker`}>
      <div>
        <Label>Catégorie principale{star}</Label>
        <Select value={catId} onValueChange={(v) => onChange?.({ category_id: v, subcategory_id: "" })}>
          <SelectTrigger data-testid={`${testIdPrefix}-category-select`}>
            <SelectValue placeholder="Choisir une catégorie"/>
          </SelectTrigger>
          <SelectContent>
            {cats.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`${testIdPrefix}-category-opt-${c.slug}`}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Sous-catégorie{star}</Label>
        <Select value={subId} onValueChange={(v) => onChange?.({ category_id: catId, subcategory_id: v })}
                disabled={!catId}>
          <SelectTrigger data-testid={`${testIdPrefix}-subcategory-select`}>
            <SelectValue placeholder={catId ? "Choisir une sous-catégorie" : "Choisissez d'abord une catégorie"}/>
          </SelectTrigger>
          <SelectContent>
            {subs.map((s) => (
              <SelectItem key={s.id} value={s.id} data-testid={`${testIdPrefix}-subcategory-opt-${s.slug}`}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
